import { useEffect, useRef } from 'react';
import { subscribeDeckEvents } from '../api/deck-events-shared';
import { apiFetch } from '../api/auth-fetch';

/**
 * Activity timeline driver — CF rebuild adapter.
 *
 * Original PP/arx contract: long-lived SSE on
 * `/api/decks/:id/activity` plus REST backfill from
 * `/api/decks/:id/activity-history`. Both speak the Arx event
 * schema (tool_use / tool_result / thinking / done).
 *
 * CF rebuild:
 *   - SSE channel is `GET /api/decks/:id/events` with NAMED
 *     events `message`, `activity`, `turn`. Activity payloads
 *     have shape `{kind, payload}` where kind ∈
 *     {turn_started, agent_thinking, agent_reply, message_delta,
 *      tool_use, tool_result, turn_ended, error}.
 *   - REST backfill is `GET /api/decks/:id/activity` returning
 *     `{activity: ActivityRow[]}` (JSON, not SSE).
 *
 * We translate both into the PP ActivityPayload / DonePayload
 * shapes so consumers (App.tsx etc.) need no changes.
 */

export interface ActivityEvent {
  type: string;
  phase?: string;
  tool?: string;
  content?: string;
  agent?: string;
  agent_id?: string;
  turn_id?: string;
  summary?: string;
  duration_ms?: number;
  id?: string;
  ts?: number;
  reason?: string;
  [key: string]: unknown;
}

export interface ActivityPayload {
  phase: string;
  tool: string;
  content: string;
  agent: string;
  agentId: string;
  turnId: string;
  /**
   * Wall-clock epoch ms when the event was produced.
   *
   * Live SSE: parsed from row.created_at when present, else Date.now()
   * (close enough — the event just arrived).
   *
   * History replay: row.created_at, so the resulting TurnRecord's
   * startedAt reflects the original time. Without this, every
   * replayed turn anchors at "moment of reload" and sorts to the
   * bottom of chat below all messages.
   * (aligns PP main #115 / 2.34.3)
   */
  ts: number;
}

export interface DonePayload {
  agent: string;
  agentId: string;
  turnId: string;
  durationMs: number;
  summary: string;
  /** Wall-clock epoch ms when the done event fired. See `ActivityPayload.ts`. */
  ts: number;
}

export interface DeckActivityHandlers {
  onActivity?: (a: ActivityPayload) => void;
  onDone?: (d: DonePayload) => void;
  onConnected?: () => void;
  onDisconnected?: (reason: string) => void;
  onHistoryLoaded?: () => void;
}

interface CfActivityRow {
  id: string;
  turn_id: string | null;
  kind: string;
  payload: { message_id?: string; delta?: string; detail?: string; message?: string };
  created_at: number;
}

const AGENT_NAME = 'pp-writer';

function rowToActivity(row: CfActivityRow): ActivityPayload | DonePayload | null {
  const turnId = row.turn_id || '';
  const agent = AGENT_NAME;
  const agentId = AGENT_NAME;
  // Recover wall-clock from the row so replayed events sort
  // chronologically against persisted chat messages (#115). PP-CF
  // stores created_at as epoch ms already; falls back to now() if
  // somehow missing/zero so legacy rows still render in best-effort
  // order rather than NaN-sorting.
  const ts = Number.isFinite(row.created_at) && row.created_at > 0
    ? row.created_at
    : Date.now();
  switch (row.kind) {
    case 'agent_thinking':
      return {
        phase: 'thinking',
        tool: 'thinking',
        content: row.payload.detail || '',
        agent,
        agentId,
        turnId,
        ts,
      };
    case 'message_delta':
      return {
        phase: 'streaming',
        tool: 'content',
        content: row.payload.delta || '',
        agent,
        agentId,
        turnId,
        ts,
      };
    case 'tool_use':
    case 'tool_result':
      return {
        phase: row.kind,
        tool: 'tool',
        content: JSON.stringify(row.payload),
        agent,
        agentId,
        turnId,
        ts,
      };
    case 'error':
      return {
        phase: 'error',
        tool: '',
        content: row.payload.message || '',
        agent,
        agentId,
        turnId,
        ts,
      };
    case 'turn_ended':
      return { agent, agentId, turnId, durationMs: 0, summary: '', ts };
    default:
      return null;
  }
}

function isDonePayload(
  p: ActivityPayload | DonePayload,
): p is DonePayload {
  return (p as DonePayload).durationMs !== undefined;
}

export function useDeckActivity(
  deckId: string | null,
  handlers: DeckActivityHandlers,
) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!deckId) return;

    let cancelled = false;
    let es: EventSource | null = null;
    let pendingRetry: ReturnType<typeof setTimeout> | null = null;

    // Backfill from REST.
    void (async () => {
      try {
        const res = await apiFetch(`/api/decks/${deckId}/activity`);
        if (cancelled || !res.ok) {
          if (!cancelled) handlersRef.current.onHistoryLoaded?.();
          return;
        }
        const body = (await res.json()) as { activity: CfActivityRow[] };
        for (const row of body.activity ?? []) {
          if (cancelled) return;
          const out = rowToActivity(row);
          if (!out) continue;
          if (isDonePayload(out)) handlersRef.current.onDone?.(out);
          else handlersRef.current.onActivity?.(out);
        }
        if (!cancelled) handlersRef.current.onHistoryLoaded?.();
      } catch {
        if (!cancelled) handlersRef.current.onHistoryLoaded?.();
      }
    })();

    const sub = subscribeDeckEvents(deckId);
    sub.onOpen(() => handlersRef.current.onConnected?.());
    sub.on('activity', (e) => {
        try {
          const data = JSON.parse(e.data) as CfActivityRow;
          const out = rowToActivity(data);
          if (!out) return;
          if (isDonePayload(out)) handlersRef.current.onDone?.(out);
          else handlersRef.current.onActivity?.(out);
        } catch {
          /* ignore */
        }
    });

    return () => {
      cancelled = true;
      sub.close();
    };
  }, [deckId]);
}
